import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { Box, Button, Paper, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';

import * as amplitude from '@amplitude/analytics-browser';

import { successPageStyles } from '../components/ComponentStyles';
import { useAppState } from '../contexts/AppStateProvider';
// Use new hooks instead of legacy context
import { useJWT } from '../contexts/JWTProvider';
import { ServiceThemeOptions } from '../themes';
import { Analytics, createAnalyticsContext } from '../utils/analytics';
import { showOnboardNotification } from '../utils/notificationUtils';
import { safeRedirect } from '../utils/urlUtils';

export default function SuccessPage() {
  const theme = useTheme() as ServiceThemeOptions;
  const navigate = useNavigate();
  const { state, search } = useLocation();

  // Use new centralized hooks
  const jwt = useJWT();
  const { serviceName: appServiceName } = useAppState();

  // Use JWT service name first (now properly restored from localStorage), then app state fallback
  const serviceName = jwt.serviceName || appServiceName;

  // Extract theme values safely
  const successColor = (theme.palette?.success as any)?.main || '#4CAF50';
  const paperBgColor = theme.palette?.background?.paper || '#FFFFFF';

  // Track page view
  useEffect(() => {
    amplitude.track('Success Page View', {
      serviceName: serviceName,
      successMessage: state?.successMsg,
      successCaption: state?.successCaption,
    });

    // Track page view in analytics system (handles both Amplitude and Slack)
    const analyticsContext = createAnalyticsContext(jwt, serviceName);
    Analytics.pageView(analyticsContext, 'Success');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    serviceName,
    state,
    // Include specific JWT properties used for analytics
    jwt.serviceId,
    jwt.fleetCode,
    jwt.rawToken,
  ]); // Performance optimized: specific JWT properties included for analytics context

  // Handle return to service
  const handleReturnToService = () => {
    amplitude.track('User Returned to Service from Success', {
      serviceName: serviceName,
      successMessage: state?.successMsg,
      redirectURL: jwt.redirectURISuccess,
    });

    // Check if both redirect URIs are null (new window scenario)
    const hasSuccessRedirect =
      jwt.redirectURISuccess && jwt.redirectURISuccess !== 'null';
    const hasErrorRedirect =
      jwt.redirectURIError && jwt.redirectURIError !== 'null';

    if (!hasSuccessRedirect && !hasErrorRedirect) {
      // New window scenario - try to close the window
      try {
        // Try to close the window first
        window.close();

        // If window.close() didn't work (popup blockers, etc.),
        // show a message after a brief delay
        setTimeout(() => {
          showOnboardNotification('Complete');
        }, 100);
      } catch (error) {
        // Fallback: show close message
        showOnboardNotification('Complete');
      }
      return;
    }

    // Use JWT success redirect, then error redirect as fallback
    const redirectURL = jwt.redirectURISuccess || jwt.redirectURIError || '/';

    if (redirectURL.startsWith('http')) {
      safeRedirect(redirectURL);
    } else {
      navigate(redirectURL);
    }
  };

  // Handle adding another provider
  const handleAddAnother = () => {
    amplitude.track('User Chose to Add Another Provider', {
      serviceName: serviceName,
      successMessage: state?.successMsg,
    });

    // Track that user wants to add another TSP (Analytics handles both Amplitude and Slack)
    const analyticsContext = createAnalyticsContext(jwt, serviceName);
    Analytics.pageView(analyticsContext, 'AddAnotherProvider');

    // Navigate back to TSP selection, preserving the URL parameters
    navigate({
      pathname: '/selectTSP',
      search: search,
    });
  };

  // Handle closing the window/tab
  const handleCloseWindow = () => {
    amplitude.track('User Clicked Close Window from Success', {
      serviceName: serviceName,
      successMessage: state?.successMsg,
    });

    try {
      // Try to close the window first
      window.close();

      // If window.close() didn't work (popup blockers, etc.),
      // show a message after a brief delay
      setTimeout(() => {
        showOnboardNotification('Complete');
      }, 100);
    } catch (error) {
      // Fallback: show close message
      showOnboardNotification('Complete');
    }
  };

  // Check if we can redirect back to service
  const canRedirectToService = jwt.canRedirectToService;

  return (
    <Box sx={successPageStyles.mainContainer}>
      <Paper
        elevation={3}
        sx={{
          ...successPageStyles.paper,
          bgcolor: paperBgColor,
        }}
      >
        {/* Icon Container */}
        <Box sx={successPageStyles.iconContainer}>
          <CheckCircleOutlineIcon
            sx={{
              fontSize: 94,
              color: successColor,
            }}
          />
        </Box>

        {/* Success Message */}
        <Typography
          variant='h4'
          sx={{
            ...successPageStyles.title(theme as any),
          }}
        >
          {state?.successMsg || 'Your account has been successfully linked'}
        </Typography>

        {/* Success Caption */}
        <Typography
          variant='body1'
          sx={{
            ...successPageStyles.caption(theme as any),
          }}
        >
          {state?.successCaption ||
            'Your telematics data is now connected and ready to use.'}
        </Typography>

        {/* Buttons Stack */}
        <Stack spacing={1.25} sx={successPageStyles.buttonsContainer}>
          {canRedirectToService ? (
            <Button
              variant='contained'
              onClick={handleReturnToService}
              sx={successPageStyles.primaryButton(theme as any)}
            >
              RETURN TO {serviceName?.toUpperCase() || 'SERVICE'}
            </Button>
          ) : (
            <Button
              variant='text'
              onClick={handleCloseWindow}
              sx={{
                textAlign: 'center',
                fontStyle: 'italic',
                color: 'text.secondary',
                mt: 2,
                p: 2,
                bgcolor: 'action.hover',
                borderRadius: 1,
                textTransform: 'none',
                fontSize: '0.875rem',
                lineHeight: 1.43,
                cursor: 'pointer',
                '&:hover': {
                  bgcolor: 'action.selected',
                  color: 'text.primary',
                },
                minHeight: 'auto',
              }}
            >
              You can safely close this tab or window.
            </Button>
          )}

          <Button
            variant='text'
            onClick={handleAddAnother}
            sx={successPageStyles.secondaryButton(theme as any)}
          >
            ADD ANOTHER PROVIDER
          </Button>
        </Stack>
      </Paper>
    </Box>
  );
}
